/**
 * Pure view model of the quotas screen (И6, task T.C4b; reworked for T.V1).
 *
 * Since owner wave 7 the plan of a place is TWO numbers — offline and online, set per
 * place, with no programme attached (migration `0010_venue_plans`) — so the grid is
 * venue | План офлайн | План онлайн | Итого, sorted and edited in place. The per-programme
 * helpers the old grid needed are gone; what stays besides the plan grid is the transfer
 * dialog (#14) and the course vocabulary the «Распределение по ЦА» headers borrow
 * (`programTitle` / `formatTag` / `buildCoursePills`).
 *
 * Framework-free on purpose: the `unit` Playwright project runs in plain Node,
 * where neither React nor `import.meta.env` exists — which is why nothing here
 * value-imports the api layer (type-only imports are erased at runtime).
 *
 * The client-side rules are the API'S OWN (`apps/api/src/**`/dto.ts), mirrored so the
 * dialog can say "no" before the round trip: unknown venue or course, a half-specified
 * scope and an empty reason are 400 VALIDATION there, a donor that cannot cover the
 * amount is 409 CONFLICT. The api stays the authority — whatever it answers anyway is
 * surfaced verbatim, never re-interpreted.
 */

import type { CourseFormat, TransferCreateInput, TransferDto } from '../../api/quotas';
import type { ComboboxOption } from '../../components/combobox-model';

// ---------------------------------------------------------------------------
// Query params this page owns
// ---------------------------------------------------------------------------

export const PARAM = {
  VENUE_ID: 'venueId',
  /**
   * ADDITIVE (T.Z56) — «Отдел» (`OP|OS|NONE|MIXED`). CLIENT-SIDE here: `GET
   * /api/venue-plans` answers the whole roster in ONE read and takes no query today, so
   * the grid filters in the browser over the venue → manager-department join, and
   * `planTotals` already recomputes from the surviving rows.
   */
  DEPARTMENT: 'department',
  COURSE_ID: 'courseId',
} as const;

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Canonical latin enum → the RU label the UI prints (docs/05 §4 invariant: the wire speaks latin). */
export function formatLabel(format: CourseFormat): string {
  return format === 'OFFLINE' ? 'Офлайн' : 'Онлайн';
}

// ---------------------------------------------------------------------------
// The grid (T.V1: the venue is the whole key — venue | План офлайн | План онлайн | Итого)
// ---------------------------------------------------------------------------

/** One row of the reworked grid: the venue's TWO plan numbers, nothing per programme. */
export interface QuotaGridRow {
  venueId: number;
  venueName: string;
  planOffline: number;
  planOnline: number;
  /** `planOffline + planOnline` — the grid's «Итого» (the api derives it too). */
  total: number;
  /** The row's freshness marker, ISO-8601 (`''` when the venue has no plan row yet). */
  updatedAt: string;
}

/** The two plan columns, in render order (OFFLINE first, the app's convention). */
export const PLAN_COLUMNS = [
  { key: 'planOffline', label: 'План офлайн' },
  { key: 'planOnline', label: 'План онлайн' },
] as const satisfies ReadonlyArray<{ key: PlanSortField; label: string }>;

export type PlanSortField = 'name' | 'planOffline' | 'planOnline';

export const PLAN_SORT_FIELDS: readonly PlanSortField[] = ['name', 'planOffline', 'planOnline'];

/** `a before b` when `asc`, reversed when `desc`. Venues break ties by id (stable pages). */
export function comparePlanRows(a: QuotaGridRow, b: QuotaGridRow, field: PlanSortField, desc: boolean): number {
  const factor = desc ? -1 : 1;
  const primary =
    field === 'name'
      ? a.venueName.localeCompare(b.venueName, 'ru')
      : a[field] - b[field];
  // The id tiebreak is NOT reversed by the direction: a descending sort must not read
  // its ties backwards, or a page could repeat a venue the previous page ended on.
  if (primary !== 0) return factor * primary;
  return a.venueId - b.venueId;
}

/**
 * Sorts the grid rows. The api answers in venue-id order, so an unsorted grid is
 * `name asc` by default — the roster order a human scans — and a click on a plan
 * column puts the biggest plans on top first (a plan column starts `desc`, a name
 * column starts `asc`), because that is the question each column answers.
 */
export function sortPlanRows(rows: QuotaGridRow[], field: PlanSortField = 'name', desc = false): QuotaGridRow[] {
  return [...rows].sort((a, b) => comparePlanRows(a, b, field, desc));
}

/** The direction the FIRST click on a column produces (see {@link sortPlanRows}). */
export function firstClickDirection(field: PlanSortField): boolean {
  return field !== 'name';
}

/** The totals row: per-format sums over the rows in scope, plus the grand total. */
export interface PlanTotals {
  offline: number;
  online: number;
  total: number;
}

export function planTotals(rows: QuotaGridRow[]): PlanTotals {
  return rows.reduce<PlanTotals>(
    (acc, row) => ({ offline: acc.offline + row.planOffline, online: acc.online + row.planOnline, total: acc.total + row.total }),
    { offline: 0, online: 0, total: 0 },
  );
}

/** One picker row: an id and the name that labels it. */
export interface IdNameOption {
  id: number;
  name: string;
}

/** The venue's row off the unfiltered read — the scoped view and the names resolve here. */
export function planRowOf(rows: QuotaGridRow[], venueId: number | undefined): QuotaGridRow | undefined {
  if (venueId === undefined) return undefined;
  return rows.find((row) => row.venueId === venueId);
}

/** The «Площадка» options: one per venue row, id-ordered, labelled by the master name. */
export function planVenueOptions(rows: QuotaGridRow[]): IdNameOption[] {
  const byId = new Map<number, string>();
  for (const row of rows) if (!byId.has(row.venueId)) byId.set(row.venueId, row.venueName);
  return [...byId.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.id - b.id);
}

export function venueNameOf(rows: QuotaGridRow[], venueId: number): string {
  return rows.find((row) => row.venueId === venueId)?.venueName ?? `Площадка ${venueId}`;
}

/**
 * The place page a venue name opens: the card is a QUERY PARAM on the roster
 * (`/venues?id=12`), so a plain router link is the whole drill.
 */
export function venueCardHref(venueId: number): string {
  return `/venues?id=${venueId}`;
}

// ---------------------------------------------------------------------------
// The «Площадка» combobox (T.R4) — the venue dictionary as type-ahead options
// ---------------------------------------------------------------------------

/**
 * One combobox row: the venue name is what the input shows and what is matched
 * first, the normalized address the secondary searchable line (`filterComboboxOptions`
 * ranks name matches above address-only matches).
 */
export function venueComboboxOptions(
  entries: ReadonlyArray<{ id: number; name: string; address?: string | null }>,
): ComboboxOption[] {
  return entries.map((entry) => ({
    value: String(entry.id),
    label: entry.name,
    hint: entry.address ?? undefined,
  }));
}

// ---------------------------------------------------------------------------
// The program pills (task T.R4) — the course dictionary grouped by ДОП family
// ---------------------------------------------------------------------------
//
// GROUPING SOURCE (the owner's constraint: never hardcode the course ids):
//   1. contract #21 `GET /api/programs` is THE source — `audience` and `formats`
//      come off the dictionary row, the family off the ДОП title;
//   2. a dictionary row may carry no metadata yet (`audience: null`,
//      `formats: []`): the audience is then parsed out of the title
//      («…для 8-9 кл.» / «…для студентов СПО») and the formats come from the
//      grid's own rows for that courseId (the `formatsFallback` argument);
//   3. when #21 answers nothing at all (endpoint not deployed yet) the caller
//      falls back to `programsFromGrid(rows)` — the same grouping over the slice
//      the quota grid already knows.
//   The courseId ranges of docs/02 §8 (31599–31613 = РИР, 31614–31619 = ИПП) are
//   the LAST resort, used only to place a course whose title names neither
//   family. A documented escape hatch, never the primary key.

/** The two ДОП families the campaign runs, plus the honest overflow bucket. */
export type CourseFamilyKey = 'RIIR' | 'IPP' | 'OTHER';

export interface CoursePillOption {
  courseId: number;
  /** Short pill label — the audience, plus the format where the variants differ. */
  label: string;
  /** The full ДОП title: the pill's tooltip, so a short label never hides a course. */
  title: string;
}

export interface CourseGroup {
  key: CourseFamilyKey;
  /** The caption printed above the group's pills, colon included. */
  title: string;
  options: CoursePillOption[];
}

/** What the pills need of a program — `ProgramDto` (#21) is structurally enough. */
export interface CoursePillSource {
  courseId: number;
  name: string;
  audience: string | null;
  formats: readonly CourseFormat[];
  /** `false` hides the variant — a course the dictionary retired. */
  active?: boolean;
  /**
   * ADDITIVE (v1.8.0, A10) — the short caption the ЦА distribution headers show
   * (`programs.short_name`); absent/null ⇒ that page keeps the courseId as its caption.
   */
  shortName?: string | null;
}

/** The family captions, in the order the owner lists them (РИР first). */
const FAMILIES: ReadonlyArray<{ key: CourseFamilyKey; pattern: RegExp; title: string; order: number }> = [
  {
    key: 'RIIR',
    pattern: /интеллектуальных\s+решений/i,
    title: 'Разработчик интеллектуальных решений:',
    order: 0,
  },
  {
    key: 'IPP',
    pattern: /инженер\s+программных\s+проектов/i,
    title: 'Инженер программных проектов на Python:',
    order: 1,
  },
];

const OTHER_GROUP_TITLE = 'Другие программы:';

/**
 * docs/02 §8 as the last resort: 31599–31613 are РИР course ids, 31614–31619 the
 * ИПП ones. Only consulted when the title names neither family.
 */
const RANGE_FALLBACK: ReadonlyArray<{ max: number; key: CourseFamilyKey }> = [
  { max: 31613, key: 'RIIR' },
  { max: 31619, key: 'IPP' },
];

/** The pill vocabulary of an audience — the order the pills list them in. */
const AUDIENCES: ReadonlyArray<{ pattern: RegExp; label: string; order: number }> = [
  { pattern: /8\s*[-–—]\s*9/, label: '8-9 кл', order: 0 },
  { pattern: /10\s*[-–—]\s*11/, label: '10-11 кл', order: 1 },
  // The dictionary spells the college variant «college», the sheets «колледж»,
  // the campaign calls both СПО — one pill word for one audience.
  { pattern: /спо|колледж|college/i, label: 'СПО', order: 2 },
];

const UNKNOWN_AUDIENCE_ORDER = 3;

/** The ДОП titles mark an online-only variant with a «(онлайн)» suffix. */
const NAME_ONLINE = /\(\s*онлайн\s*\)/i;

/**
 * `Program.name` is not nullable in the schema, but a provisional row may carry '': the
 * «Распределение по ЦА» headers and the transfer dialog's course list print through this,
 * and a blank name must never print as an empty column caption.
 */
export function programTitle(courseId: number, programName: string): string {
  const name = programName.trim();
  return name === '' ? `Курс ${courseId}` : name;
}

/** The grid header reads the same «офл/онл» tags the filter pills use (T.R4). */
export function formatTag(format: CourseFormat): string {
  return FORMAT_TAGS[format];
}

const FORMAT_TAGS: Readonly<Record<CourseFormat, string>> = { OFFLINE: 'офл', ONLINE: 'онл' };
const FORMAT_ORDER: Readonly<Record<CourseFormat, number>> = { OFFLINE: 0, ONLINE: 1 };
/**
 * The canonical vocabulary OFFLINE-first, spelled out here because this module
 * value-imports nothing from the api layer (see the header) — the api's own
 * `COURSE_FORMATS` carries the same order.
 */
const FORMATS_ORDERED: readonly CourseFormat[] = ['OFFLINE', 'ONLINE'];

/** The audience a course addresses — the dictionary's word first, then the title. */
export function audienceLabel(audience: string | null, name: string): string | null {
  const haystack = `${audience ?? ''} ${name}`;
  return AUDIENCES.find((entry) => entry.pattern.test(haystack))?.label ?? null;
}

/** The family a course belongs to — by title, or by the §8 id ranges as the last resort. */
export function courseFamily(courseId: number, name: string): CourseFamilyKey {
  const family = FAMILIES.find((entry) => entry.pattern.test(name));
  if (family) return family.key;
  return RANGE_FALLBACK.find((entry) => courseId <= entry.max)?.key ?? 'OTHER';
}

function familyTitle(key: CourseFamilyKey): string {
  return FAMILIES.find((family) => family.key === key)?.title ?? OTHER_GROUP_TITLE;
}

function familyOrder(key: CourseFamilyKey): number {
  return FAMILIES.find((family) => family.key === key)?.order ?? FAMILIES.length;
}

/**
 * The formats one variant runs: the dictionary's word, else the grid's rows for
 * that courseId, else the title's «(онлайн)» marker.
 */
function formatsOf(
  program: CoursePillSource,
  formatsFallback: ((courseId: number) => readonly CourseFormat[]) | undefined,
): CourseFormat[] {
  if (program.formats.length > 0) return FORMATS_ORDERED.filter((format) => program.formats.includes(format));
  const fromGrid = formatsFallback?.(program.courseId) ?? [];
  if (fromGrid.length > 0) return FORMATS_ORDERED.filter((format) => fromGrid.includes(format));
  return NAME_ONLINE.test(program.name) ? ['ONLINE'] : ['OFFLINE'];
}

/**
 * The pill groups, captions in family order, pills inside a group by audience
 * then format. A pill exists for every ACTIVE program of the dictionary — a
 * course with no quota rows yet still filters (an honest empty grid beats a
 * variant the user cannot reach). The label carries a format tag only where one
 * audience holds several variants: a lone «10-11 кл» needs no «· офл».
 */
export function buildCoursePills(
  programs: readonly CoursePillSource[],
  formatsFallback?: (courseId: number) => readonly CourseFormat[],
): CourseGroup[] {
  const buckets = new Map<CourseFamilyKey, CoursePillSource[]>();
  for (const program of programs) {
    if (program.active === false) continue;
    const key = courseFamily(program.courseId, program.name);
    const bucket = buckets.get(key) ?? [];
    bucket.push(program);
    buckets.set(key, bucket);
  }

  const groups: CourseGroup[] = [];
  for (const key of [...buckets.keys()].sort((a, b) => familyOrder(a) - familyOrder(b))) {
    const bucket = buckets.get(key) ?? [];
    if (bucket.length === 0) continue;

    const variants = bucket.map((program) => ({
      courseId: program.courseId,
      title: programTitle(program.courseId, program.name),
      audience: audienceLabel(program.audience, program.name),
      formats: formatsOf(program, formatsFallback),
    }));

    // How many pills one audience produces inside this group — the switch that
    // turns the format tag on.
    const pillsPerAudience = new Map<string, number>();
    for (const variant of variants) {
      if (variant.audience === null) continue;
      pillsPerAudience.set(
        variant.audience,
        (pillsPerAudience.get(variant.audience) ?? 0) + variant.formats.length,
      );
    }

    interface PillDraft {
      courseId: number;
      title: string;
      label: string;
      audienceOrder: number;
      format: CourseFormat | undefined;
    }

    const drafts: PillDraft[] = [];
    for (const variant of variants) {
      if (variant.audience === null) {
        // No audience anywhere — the ДОП title is the only honest label.
        drafts.push({
          courseId: variant.courseId,
          title: variant.title,
          label: variant.title,
          audienceOrder: UNKNOWN_AUDIENCE_ORDER,
          format: undefined,
        });
        continue;
      }
      const audienceOrder =
        AUDIENCES.find((entry) => entry.label === variant.audience)?.order ?? UNKNOWN_AUDIENCE_ORDER;
      const tagged = (pillsPerAudience.get(variant.audience) ?? 0) > 1;
      // An untagged pill prints the audience once, however many formats the
      // variant runs — one pill per course is what the filter selects.
      const formats = tagged ? variant.formats : [variant.formats[0] ?? 'OFFLINE'];
      for (const format of formats) {
        drafts.push({
          courseId: variant.courseId,
          title: variant.title,
          label: tagged ? `${variant.audience} · ${FORMAT_TAGS[format]}` : variant.audience,
          audienceOrder,
          format,
        });
      }
    }

    drafts.sort(
      (a, b) =>
        a.audienceOrder - b.audienceOrder ||
        FORMAT_ORDER[a.format ?? 'OFFLINE'] - FORMAT_ORDER[b.format ?? 'OFFLINE'] ||
        a.courseId - b.courseId,
    );

    const options: CoursePillOption[] = drafts.map(({ courseId, title, label }) => ({ courseId, title, label }));
    groups.push({ key, title: familyTitle(key), options });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// ADDITIVE (T.Z149, owner 20.09) — the partner filials leave the grid; their SHARED
// pool becomes the notification block at the top of the page. A partner's filials hold
// NO quotas of their own (`partners.plan_online` is the ONE denominator), so a grid
// row per filial misrepresents them: the rows leave, the pool arrives as one block
// that edits #29 — the same write the settings tab has always made.
// ---------------------------------------------------------------------------

/** The minimal directory slice the partner folds read (`VenueDirectoryEntry` is enough). */
export interface PartnerDirectoryEntry {
  id: number;
  kind?: string;
  partnerId?: number | null;
  partnerName?: string | null;
  /** The partner's SHARED pool as #6 carries it — every filial of a partner repeats it. */
  partnerPlanOnline?: number | null;
}

/** One partner's shared pool, seeded from the venue directory (#6). */
export interface PartnerPoolEntry {
  partnerId: number;
  /** `partners.name` — the caption's first word. */
  partnerName: string;
  /** `partners.plan_online` — the SHARED quota of all the partner's filials. */
  planOnline: number;
  /** The partner's filials — the grid rows the page gives up to the block. */
  venues: number;
  /** The filials' venue ids — exactly what the grid filters out. */
  venueIds: number[];
}

/**
 * The partners behind the directory's `PARTNER_ONLINE` venues, one entry per partner in
 * `partnerId` order. The pool is read ONCE per partner (a filial's `partnerPlanOnline`
 * is the shared number repeated, never a sum), the name from the first filial that
 * carries it. A directory entry without a `partnerId` joins nothing (an older api reads
 * as REGISTRY anyway).
 */
export function partnerPoolsOfDirectory(entries: ReadonlyArray<PartnerDirectoryEntry>): PartnerPoolEntry[] {
  interface PoolAcc {
    partnerId: number;
    partnerName: string;
    planOnline: number | null;
    venueIds: number[];
  }
  const pools = new Map<number, PoolAcc>();
  for (const entry of entries) {
    if (entry.kind !== 'PARTNER_ONLINE' || entry.partnerId === null || entry.partnerId === undefined) continue;
    const existing = pools.get(entry.partnerId);
    if (existing) {
      existing.venueIds.push(entry.id);
      if (existing.partnerName === '' && entry.partnerName) existing.partnerName = entry.partnerName;
      continue;
    }
    pools.set(entry.partnerId, {
      partnerId: entry.partnerId,
      partnerName: entry.partnerName ?? '',
      planOnline: entry.partnerPlanOnline ?? null,
      venueIds: [entry.id],
    });
  }
  return [...pools.values()]
    .sort((left, right) => left.partnerId - right.partnerId)
    .map((pool) => ({
      partnerId: pool.partnerId,
      partnerName: pool.partnerName,
      planOnline: pool.planOnline ?? 0,
      venues: pool.venueIds.length,
      venueIds: pool.venueIds,
    }));
}

/** The venue ids a partner filial holds — the set the quota grid filters out (T.Z149). */
export function partnerVenueIdsOf(entries: ReadonlyArray<PartnerDirectoryEntry>): Set<number> {
  return new Set(partnerPoolsOfDirectory(entries).flatMap((pool) => pool.venueIds));
}

/** The grid rows whose venue is NOT a partner filial (the rest of the page never sees them). */
export function rowsWithoutPartnerVenues<T extends { venueId: number }>(
  rows: ReadonlyArray<T>,
  partnerVenueIds: ReadonlySet<number>,
): T[] {
  if (partnerVenueIds.size === 0) return [...rows];
  return rows.filter((row) => !partnerVenueIds.has(row.venueId));
}

/** The block's caption in the owner's own words: «Академия Топ: общая квота 2 600». */
export function partnerPoolCaption(partnerName: string, planOnline: number): string {
  const name = partnerName.trim() === '' ? 'Партнёр' : partnerName;
  return `${name}: общая квота ${planOnline.toLocaleString('ru-RU')}`;
}

// ---------------------------------------------------------------------------
// The inline plan edit (#13)
// ---------------------------------------------------------------------------

export interface PlanInputResult {
  /** The parsed plan, `null` when the draft is empty or invalid. */
  value: number | null;
  error: string | undefined;
  /** `true` when the draft may go out as a PUT body. */
  canSubmit: boolean;
}

/** Same rule the api applies to `body.plan` (`parseQuotaUpsertBody`): a non-negative integer. */
export const PLAN_ERROR = 'План — целое число не меньше нуля';

/**
 * An EMPTY draft is not a 0: «0» is a legal, deliberate «no seats here» row, and
 * wiping a cell by accident must not silently zero a venue's plan.
 */
export function parsePlanInput(raw: string): PlanInputResult {
  const value = raw.trim();
  if (value === '') return { value: null, error: undefined, canSubmit: false };
  if (!/^\d+$/.test(value)) return { value: null, error: PLAN_ERROR, canSubmit: false };
  return { value: Number(value), error: undefined, canSubmit: true };
}

/** `false` for a draft that would not change the stored plan — the api answers a no-op with the row. */
export function planChanged(current: number | undefined, next: number): boolean {
  return current !== next;
}

// ---------------------------------------------------------------------------
// The transfer dialog (#14)
// ---------------------------------------------------------------------------

/**
 * `VENUE_LEVEL` = both scope keys absent (pro-rata over BOTH formats), `FORMAT` = one
 * format only. The course axis the old `EXACT` scope named is gone from the plan model
 * (T.V1), so there is nothing left to pin a course to.
 */
export type TransferScope = 'VENUE_LEVEL' | 'FORMAT';

export type TransferField = 'fromVenueId' | 'toVenueId' | 'amount' | 'scope' | 'format' | 'reason';

export interface TransferDraft {
  fromVenueId: string;
  toVenueId: string;
  amount: string;
  scope: TransferScope;
  /** `''` = no format chosen (legal only for a venue-level transfer). */
  format: CourseFormat | '';
  reason: string;
}

export function emptyTransferDraft(): TransferDraft {
  return { fromVenueId: '', toVenueId: '', amount: '', scope: 'VENUE_LEVEL', format: '', reason: '' };
}

export interface TransferValidation {
  /** The POST body, or `null` while anything is wrong. */
  body: TransferCreateInput | null;
  errors: Partial<Record<TransferField, string>>;
  /** `true` when the body is complete and no field is invalid. */
  canSubmit: boolean;
}

export const TRANSFER_ERRORS = {
  DONOR: 'Выберите площадку-донора',
  RECEIVER: 'Выберите площадку-получателя',
  SAME: 'Площадки должны различаться — перевод внутри одной площадки невозможен',
  AMOUNT: 'Количество мест — целое число больше нуля',
  FORMAT: 'Выберите формат — либо весь перевод пропорционально, либо один формат',
  REASON: 'Укажите причину перевода',
} as const;

/**
 * Mirrors `parseTransferBody` of the api: both venues (positive integers), a
 * positive integer amount, a non-empty reason, and `courseId`+`format` given
 * together or not at all. One invalid field blocks the whole submit.
 */
export function validateTransferDraft(draft: TransferDraft): TransferValidation {
  const errors: Partial<Record<TransferField, string>> = {};

  const fromVenueId = Number.parseInt(draft.fromVenueId, 10);
  if (!Number.isInteger(fromVenueId) || fromVenueId < 1) errors.fromVenueId = TRANSFER_ERRORS.DONOR;

  const toVenueId = Number.parseInt(draft.toVenueId, 10);
  if (!Number.isInteger(toVenueId) || toVenueId < 1) errors.toVenueId = TRANSFER_ERRORS.RECEIVER;

  if (Number.isInteger(fromVenueId) && fromVenueId === toVenueId) {
    errors.toVenueId = TRANSFER_ERRORS.SAME;
  }

  const amountRaw = draft.amount.trim();
  const amount = Number.parseInt(amountRaw, 10);
  if (!/^\d+$/.test(amountRaw) || !Number.isInteger(amount) || amount < 1) {
    errors.amount = TRANSFER_ERRORS.AMOUNT;
  }

  const formatScoped = draft.scope === 'FORMAT';
  if (formatScoped && draft.format === '') {
    errors.format = TRANSFER_ERRORS.FORMAT;
  }

  const reason = draft.reason.trim();
  if (reason === '') errors.reason = TRANSFER_ERRORS.REASON;

  const clean = Object.keys(errors).length === 0;
  return {
    body: clean
      ? {
          fromVenueId,
          toVenueId,
          amount,
          // No `courseId` travels: the plan has no programme axis (T.V1), and the api
          // answers a format-scoped body without one since v1.6.
          ...(formatScoped ? { format: draft.format as CourseFormat } : {}),
          reason,
        }
      : null,
    errors,
    canSubmit: clean,
  };
}

// ---------------------------------------------------------------------------
// The ledger (#15)
// ---------------------------------------------------------------------------

/**
 * The scope column of the ledger. Since T.V1 a row with a `format` moved THAT format
 * (the `courseId` a legacy row carries scoped the move once, and prints next to it as
 * history); a row without one moved the venue's whole plan, pro-rata.
 */
export function transferScopeLabel(transfer: Pick<TransferDto, 'courseId' | 'format'>): string {
  if (transfer.format === null) return 'вся площадка';
  const label = formatLabel(transfer.format);
  return transfer.courseId === null ? label : `${transfer.courseId} · ${label}`;
}

/** `venueId` of #15 matches EITHER direction — the badge says which way it went. */
export type TransferDirection = 'OUTGOING' | 'INCOMING' | 'OTHER';

export function transferDirection(
  transfer: Pick<TransferDto, 'fromVenueId' | 'toVenueId'>,
  venueId: number | undefined,
): TransferDirection {
  if (venueId === undefined) return 'OTHER';
  if (transfer.fromVenueId === venueId) return 'OUTGOING';
  if (transfer.toVenueId === venueId) return 'INCOMING';
  return 'OTHER';
}

export function transferDirectionLabel(direction: TransferDirection): string {
  switch (direction) {
    case 'OUTGOING':
      return 'отдано';
    case 'INCOMING':
      return 'принято';
    default:
      return '';
  }
}

/** `from → to` with the names resolved against the unfiltered grid. */
export function transferRoute(
  transfer: Pick<TransferDto, 'fromVenueId' | 'toVenueId'>,
  nameOf: (venueId: number) => string,
): string {
  return `${nameOf(transfer.fromVenueId)} → ${nameOf(transfer.toVenueId)}`;
}

/**
 * The dialog's summary of a venue-level move — the pro-rata semantics of
 * `planVenueLevelTransfer` in one sentence, so an admin signs what they meant:
 * the donor loses seats across ALL its quota rows in proportion to plan, the
 * receiver gains them along its OWN plans per format (conservation is on the
 * whole move, not per format).
 */
export function venueLevelHint(amount: number, fromName: string, toName: string): string {
  return (
    `${amount} мест уйдут со всех квот «${fromName}» пропорционально их планам ` +
    `и распределятся по квотам «${toName}» по её собственным планам (OFFLINE/ONLINE отдельно).`
  );
}
